import { Box, Typography } from "@mui/material";
import { rem } from "polished";
import React from "react";
import z from "zod";

import {
  ConfidentialityIntro,
  ConfidentialityPartnersOffersInformationOptIn,
  ConfidentialityPersonalDataOptIn,
  ConfidentialityPartnersOffersInformationOptOut,
  ConfidentialityPersonalDataOptOut,
  Modal,
  ModalFooter,
  ExternalLink,
} from "src/components";
import { EmeriaUrl } from "src/constants";
import { useAppForm, useMarketplaceLink } from "src/hooks";
import { useBrand } from "src/hooks/useBrand";

export interface ConfidentialityInputs {
  personalDataOptIn: boolean;
  partnersOffersInformationOptIn: boolean;
  personalDataOptOut: boolean;
  partnersOffersInformationOptOut: boolean;
}

interface Props {
  open: boolean;
  loading: boolean;
  onSubmit(data: ConfidentialityInputs): void;
}

export const ConfidentialityModal = ({ open, loading, onSubmit }: Props) => {
  const { identity, customerPortal } = useBrand();
  const marketplaceLink = useMarketplaceLink();

  const defaultValues: ConfidentialityInputs = {
    personalDataOptIn: false,
    partnersOffersInformationOptIn: false,
    personalDataOptOut: false,
    partnersOffersInformationOptOut: false,
  };

  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmit: z.object({
        personalDataOptIn: z.boolean(),
        personalDataOptOut: z.boolean(),
        partnersOffersInformationOptIn: z.boolean(),
        partnersOffersInformationOptOut: z.boolean(),
      }),
    },
    onSubmit: ({ value }) => onSubmit(value),
  });

  const mutuallyExclusiveFieldValidator = (oppositeFieldName: string) => ({
    onChange: ({ value, fieldApi }: { value: boolean; fieldApi: any }) => {
      if (value && fieldApi.form.getFieldValue(oppositeFieldName)) {
        fieldApi.form.setFieldValue(oppositeFieldName, false);
      }
      return undefined;
    },
  });

  return (
    <Modal
      data-cy="mandatory-consent-modal"
      hideCloseButton
      disableBackdropClick
      disableEscapeKeyDown
      icon="confidentiality"
      title="Confidentialité"
      open={open}
      handleClose={form.handleSubmit}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <Typography
          fontWeight="bold"
          variant="h3"
          sx={{ fontSize: rem(14), margin: 0, lineHeight: 1.5, textAlign: "center" }}
        >
          Bienvenue sur votre espace client {customerPortal.name}
        </Typography>

        <ConfidentialityIntro sx={{ mt: 2, textAlign: "center" }} />

        <Typography sx={{ my: 2, fontWeight: "bold" }}>Pour {identity.name} uniquement</Typography>

        <form.AppField
          name="personalDataOptIn"
          validators={mutuallyExclusiveFieldValidator("personalDataOptOut")}
        >
          {(field) => (
            <field.CheckboxField
              label={<ConfidentialityPersonalDataOptIn />}
              disabled={field.state.value}
            />
          )}
        </form.AppField>

        <form.AppField
          name="personalDataOptOut"
          validators={mutuallyExclusiveFieldValidator("personalDataOptIn")}
        >
          {(field) => (
            <field.CheckboxField
              label={<ConfidentialityPersonalDataOptOut />}
              disabled={field.state.value}
            />
          )}
        </form.AppField>

        <Typography sx={{ my: 2, fontWeight: "bold" }}>
          Pour les <ExternalLink href={EmeriaUrl}>filiales Emeria</ExternalLink> et ses{" "}
          {marketplaceLink ? (
            <ExternalLink href={marketplaceLink}>partenaires de confiance</ExternalLink>
          ) : (
            "partenaires de confiance"
          )}
        </Typography>

        <form.AppField
          name="partnersOffersInformationOptIn"
          validators={mutuallyExclusiveFieldValidator("partnersOffersInformationOptOut")}
        >
          {(field) => (
            <field.CheckboxField
              label={<ConfidentialityPartnersOffersInformationOptIn />}
              disabled={field.state.value}
            />
          )}
        </form.AppField>

        <form.AppField
          name="partnersOffersInformationOptOut"
          validators={mutuallyExclusiveFieldValidator("partnersOffersInformationOptIn")}
        >
          {(field) => (
            <field.CheckboxField
              label={<ConfidentialityPartnersOffersInformationOptOut />}
              disabled={field.state.value}
            />
          )}
        </form.AppField>

        <Typography sx={{ mt: 2, textAlign: "center" }}>
          À tout moment, vous pouvez ajuster vos préférences marketing au niveau de votre profil.
        </Typography>

        <ModalFooter>
          <form.Subscribe selector={(state) => state.values}>
            {(values) => {
              const disabled =
                (!values.personalDataOptIn && !values.personalDataOptOut) ||
                (!values.partnersOffersInformationOptIn && !values.partnersOffersInformationOptOut);

              return (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <form.SubmitButton disabled={disabled} loading={loading}>
                    Envoyer
                  </form.SubmitButton>
                  {disabled && (
                    <Typography
                      variant="caption"
                      sx={{ color: "error.main", mt: 1, textAlign: "center" }}
                    >
                      Pour pouvoir accéder à votre espace client, vous devez faire un choix :
                      profiter/économiser ou renoncer !
                    </Typography>
                  )}
                </Box>
              );
            }}
          </form.Subscribe>
        </ModalFooter>
      </form>
    </Modal>
  );
};
